<template>
  <div class="rte" :class="{ 'rte-empty': isEmpty }">
    <QuillEditor
      ref="quill"
      :content="modelValue"
      content-type="html"
      theme="snow"
      :toolbar="toolbar"
      :placeholder="placeholder"
      @update:content="onChange"
      @ready="onReady"
    />
    <div class="rte-attach">
      <button type="button" class="rte-attach-btn" @click="pdfPick">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
        {{ t('fields.attachPdf') }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import api from '@/api'
import { useToast } from '@/composables/useToast'
import { useI18n } from '@/composables/useI18n'

// Full formatting toolbar (headings, colours, lists, alignment, links, images…).
const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
]

export default {
  name: 'RichTextEditor',
  components: { QuillEditor },
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const quill = ref(null)
    let editor = null
    const toast = useToast()
    const { t } = useI18n()

    // Quill emits "<p><br></p>" for an empty doc — treat that as empty so the
    // caller stores null instead of noise.
    const isEmpty = computed(() => {
      const v = (props.modelValue || '').replace(/<(p|br|div)[^>]*>|<\/(p|div)>|\s|&nbsp;/gi, '')
      return v === ''
    })

    function onChange(html) {
      emit('update:modelValue', html === '<p><br></p>' ? '' : html)
    }

    // Upload an image File/Blob and embed it at the cursor.
    async function uploadAndInsert(file) {
      if (!editor || !file) return
      const range = editor.getSelection(true) || { index: editor.getLength() }
      try {
        const { url } = await api.uploadImage(await prepareImage(file))
        editor.insertEmbed(range.index, 'image', url, 'user')
        editor.setSelection(range.index + 1, 0, 'user')
      } catch (e) {
        toast.error(e.message || t('toast.actionFailed'))
      }
    }

    // Upload a PDF and insert a clickable filename chip (a link) at the cursor.
    async function uploadAndInsertPdf(file) {
      if (!editor || !file) return
      const range = editor.getSelection(true) || { index: editor.getLength() }
      try {
        const { url, name } = await api.uploadImage(file)
        const label = '📄 ' + (name || 'PDF')
        editor.insertText(range.index, label, 'link', url, 'user')
        editor.insertText(range.index + label.length, ' ', { link: false }, 'user')
        editor.setSelection(range.index + label.length + 1, 0, 'user')
      } catch (e) {
        toast.error(e.message || t('toast.actionFailed'))
      }
    }

    // Open a native file picker. The input is attached to the DOM until it fires:
    // iOS Safari can garbage-collect a detached input and never deliver "change".
    function pickFile(accept, onFile) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = accept
      input.style.display = 'none'
      input.onchange = () => {
        const file = input.files && input.files[0]
        input.remove()
        onFile(file)
      }
      document.body.appendChild(input)
      input.click()
    }

    function pdfPick() {
      pickFile('application/pdf,.pdf', uploadAndInsertPdf)
    }

    // Toolbar image button → native file picker → upload (not base64).
    function imagePick() {
      pickFile('image/*', uploadAndInsert)
    }

    // Phone gallery photos are often too big for the server (5 MB / 40 MP caps) or
    // HEIC, which it doesn't accept. Downscale + re-encode to JPEG in the browser
    // when needed; small web-safe images go up untouched. Falls back to the
    // original file if the browser can't decode it (server then reports why).
    const MAX_SIDE = 2560
    const PASS_BYTES = 1.5 * 1024 * 1024
    const SAFE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

    function loadImage(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')) }
        img.src = url
      })
    }

    async function prepareImage(file) {
      if (file.type === 'image/gif') return file // keep animation
      let img
      try { img = await loadImage(file) } catch (e) { return file }
      const w = img.naturalWidth, h = img.naturalHeight
      const scale = Math.min(1, MAX_SIDE / Math.max(w, h))
      if (scale === 1 && file.size <= PASS_BYTES && SAFE_TYPES.includes(file.type)) return file
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff' // JPEG has no alpha
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85))
      if (!blob) return file
      const base = (file.name || 'image').replace(/\.[^.]+$/, '')
      return new File([blob], base + '.jpg', { type: 'image/jpeg' })
    }

    // Ctrl+V of an image → intercept before Quill inlines it as base64, upload instead.
    function onPaste(e) {
      const items = (e.clipboardData && e.clipboardData.items) || []
      for (const it of items) {
        if (it.type && it.type.indexOf('image/') === 0) {
          e.preventDefault()
          const file = it.getAsFile()
          if (file) uploadAndInsert(file)
          return
        }
      }
    }

    function onReady(q) {
      editor = q
      q.getModule('toolbar').addHandler('image', imagePick)
      q.root.addEventListener('paste', onPaste, true)
    }

    return { quill, toolbar: TOOLBAR, isEmpty, onChange, onReady, pdfPick, t }
  },
}
</script>

<style scoped>
/* Match the app's transparent-border / soft-fill design instead of Quill's default
   hairline boxes. One rounded surface; toolbar sits on a faint fill. */
.rte :deep(.ql-toolbar.ql-snow),
.rte :deep(.ql-container.ql-snow) { border: 1px solid transparent; }
.rte :deep(.ql-toolbar.ql-snow) {
  background: var(--surface-2);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}
.rte :deep(.ql-container.ql-snow) {
  background: var(--surface-2);
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--text) 8%, transparent);
  font-family: var(--font);
  font-size: 14px;
  color: var(--text);
}
.rte :deep(.ql-editor) { min-height: 120px; }
/* PDF attach button below the editor. */
.rte-attach { margin-top: 6px; }
.rte-attach-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border: none; border-radius: var(--radius-sm);
  background: var(--surface-2); color: var(--accent-ink);
  font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.rte-attach-btn:hover { background: var(--accent-wash); }
.rte-attach-btn svg { color: var(--accent); }
.rte :deep(.ql-editor.ql-blank::before) { color: var(--muted); font-style: normal; }
.rte :deep(.ql-editor img) { max-width: 100%; border-radius: 8px; }
/* Toolbar icons follow the theme's ink colour (dark mode too). */
.rte :deep(.ql-snow .ql-stroke) { stroke: var(--text); }
.rte :deep(.ql-snow .ql-fill) { fill: var(--text); }
.rte :deep(.ql-snow .ql-picker) { color: var(--text); }
.rte :deep(.ql-snow.ql-toolbar button:hover .ql-stroke),
.rte :deep(.ql-snow .ql-toolbar button.ql-active .ql-stroke) { stroke: var(--accent); }
.rte :deep(.ql-snow.ql-toolbar button:hover .ql-fill),
.rte :deep(.ql-snow .ql-toolbar button.ql-active .ql-fill) { fill: var(--accent); }
/* Dropdown option panels need a solid surface (default is white, unreadable in dark). */
.rte :deep(.ql-snow .ql-picker-options) {
  background: var(--surface); color: var(--text);
  border: 1px solid var(--card-brd); border-radius: 8px;
}
</style>
